import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { LoginImg } from '../../../public/images/admin';
import { LogoIcon, EyeOpen, EyeClosed } from '../../../public/icons';

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, demoLogin, admin, loading, error, clearError } = useAdminAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (admin) {
      const from = location.state?.from || '/admin/dashboard';
      navigate(from, { replace: true });
    }
  }, [admin, navigate, location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(formData.email, formData.password);
    if (result.success) {
      navigate('/admin/dashboard', { replace: true });
    }
  };

  const handleDemoLogin = async () => {
    setIsDemoLoading(true);
    const result = await demoLogin();
    setIsDemoLoading(false);
    if (result.success) {
      navigate('/admin/dashboard', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row overflow-hidden bg-white">
      {/* Left Side - Image - Hidden on mobile, visible on larger screens */}
      <div className="hidden lg:flex lg:w-3/5 h-screen items-center justify-center p-4 ">
        <img 
          src={LoginImg} 
          alt="Login Background" 
          className="w-full h-full object-cover "
        />
      </div>

      {/* Right Side - Login Form - Full width on mobile */}
      <div className="w-full lg:w-2/5 min-h-screen  flex mt-[1.5%] justify-center ">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          {/* Logo/Brand */}
          <div className="mb-8 sm:mb-12 md:mb-16 lg:mb-20 xl:mb-32 text-center lg:text-left">
            <LogoIcon 
              className="hover:opacity-80 transition-opacity duration-200 mx-auto lg:mx-0" 
              color="#1C2A46"
              size={36}
            />
          </div>

          {/* Login Form */}
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium text-secondary mb-2 text-center lg:text-left">
              Login to your account
            </h2>
            <p className="text-sm sm:text-base text-[#6B635D] font-normal mb-6 sm:mb-8 text-center lg:text-left">
              Log in to manage products, orders, and future gifts.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg mb-4 sm:mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <p className="text-xs sm:text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <form className="space-y-4 sm:space-y-5 md:space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-left text-sm sm:text-base font-normal text-secondary mb-1 sm:mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-2.5 sm:p-3 border border-gray-300 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-xs sm:placeholder:text-sm placeholder:font-light placeholder:text-[#6B635D] rounded"
                  placeholder="admin@kapta.co"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-left text-sm sm:text-base font-normal text-secondary mb-1 sm:mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full p-2.5 sm:p-3 pr-10 sm:pr-12 border border-gray-300 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-xs sm:placeholder:text-sm placeholder:font-light placeholder:text-[#6B635D] rounded"
                    placeholder="••••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-2 sm:pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeClosed className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <EyeOpen className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || isDemoLoading}
                className="w-full bg-primary hover:bg-yellow-700 text-white font-medium py-2.5 sm:py-3 px-4 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm sm:text-base"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Logging in...
                  </>
                ) : (
                  'Log In'
                )}
              </button>
            </form>

            {/* Demo Login Button (Optional - commented out as it wasn't in original) */}
            {/* <div className="mt-4 text-center">
              <button
                onClick={handleDemoLogin}
                disabled={loading || isDemoLoading}
                className="text-sm text-gray-600 hover:text-gray-800 underline"
              >
                {isDemoLoading ? 'Loading...' : 'Try Demo Login'}
              </button>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;